import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Quote, Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/* -------------------------------------------------------------------------
   TO ADD A NEW TESTIMONIAL:
   • Video:  add to videoTestimonials. `embedUrl` accepts a YouTube embed link
     (e.g. https://www.youtube.com/embed/XXXXX) or Vimeo (https://player.vimeo.com/video/XXXX).
     Leave `embedUrl` as "" to show a "Coming soon" placeholder card.
   • Written: add to writtenTestimonials.
   ------------------------------------------------------------------------- */

const videoTestimonials = [
  { id: "v1", name: "", role: "", thumbnail: "", embedUrl: "" },
  { id: "v2", name: "", role: "", thumbnail: "", embedUrl: "" },
  { id: "v3", name: "", role: "", thumbnail: "", embedUrl: "" },
];

const writtenTestimonials = [
  {
    id: "w1",
    quote:
      "There were 3 shifts in my business under Tammy's Coaching Program:\n\n1. When I wasn't getting the results that I needed when sending out the Golden Letter and Tammy said \"double your efforts.\" Then the phone began to ring with potential sellers.\n\n2. Understanding that I'm more motivated by contributing to the success of the market center over money.\n\n3. Making sure that my activities are in alignment with my personal goals.\n\nThank you Tammy for your impact on my life! Forever grateful!",
    name: "Dana Spencer",
    role: "Real Estate Agent · KW Louisville",
    rating: 5,
  },
  {
    id: "w2",
    quote:
      "I am so excited this year to double my business! Business Coaching KW Maine is my way of being more accountable to my goals this year. I cannot say enough about how this has changed my business approach already.",
    name: "Theresa",
    role: "Coaching Client · KW Maine",
    rating: 5,
  },
  {
    id: "w3",
    quote:
      "Coaching is the process of facilitating someone's progress by encouraging self-discovery and personal growth. An effective coach employs insightful questioning to guide the client in charting their own course. An exceptional coach poses questions that resonate deeply, fostering ongoing reflection. A phenomenal coach delves into the essence of the individual, enabling the creation of not just a path forward but also new perspectives free from bias, focused solely on growth and development. Thank you for being such a phenomenal coach.",
    name: "Barry",
    role: "Coaching Client",
    rating: 5,
  },
  {
    id: "w4",
    quote:
      "Good afternoon everyone! I just want to give a heartfelt shout out to the coaches that have reached into my real estate business and ultimately my heart to help me to give the best experience possible to clients. I am thankful for all that they have added and look forward to duplicating the boatful of value and passing it on to others. This year is going to be amazing! Ready. Set. Let's Go!",
    name: "Laura",
    role: "Coaching Client",
    rating: 5,
  },
  {
    id: "w5",
    quote:
      "Yesterday was about learning new ways, unlearning some ways, and re-learning others. In leadership roles, we must be able to shift quickly and give up to go up. I am grateful for my coaches that push my mind further than before and I know they will hold me accountable to make these shifts in my mindset and skillset. I would not be the person I am today without these powerful and inspirational minds around me all the time. I'm eternally grateful for this company and for being able to see everyone in person again. What are you doing to grow? In Dianna's words, if you're not growing, you're dying.",
    name: "Sonia",
    role: "Coaching Client",
    rating: 5,
  },
];

export default function Testimonials() {
  const [activeVideo, setActiveVideo] = useState(null);

  const open = (v) => v.embedUrl && setActiveVideo(v);

  return (
    <div data-testid="testimonials-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Testimonials
          </span>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]">
            In their
            <br />
            <span className="italic text-primary">own words.</span>
          </h1>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/80 leading-relaxed">
          Stories from clients, readers, and coaching members — the people
          who've trusted Tammy with a home, a career, or a season of life.
        </p>
      </section>

      {/* Video testimonials */}
      {videoTestimonials.length > 0 && (
      <section className="bg-muted/40 py-20 md:py-24" data-testid="video-testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
                <Play size={12} /> Video Stories
              </div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                Hear it directly.
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
              Short, unedited videos from people who have worked with Tammy.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {videoTestimonials.map((v, i) => (
              <VideoCard key={v.id} v={v} i={i} onOpen={open} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Written testimonials */}
      {writtenTestimonials.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="written-testimonials">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Quote size={12} /> Written Notes
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              The quiet ones <span className="italic text-primary">stick with you.</span>
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {writtenTestimonials.map((t, i) => (
            <blockquote
              key={t.id}
              className="bg-card border border-border rounded-2xl p-8 card-lift flex flex-col"
              data-testid={`written-testimonial-${i}`}
            >
              <div className="flex items-center gap-1 text-primary">
                {[...Array(t.rating)].map((_, idx) => (
                  <Star key={`${t.id}-star-${idx}`} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <div className="font-serif italic text-lg leading-relaxed mt-5 flex-1 whitespace-pre-line text-foreground/90">
                "{t.quote}"
              </div>
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm font-medium text-foreground">{t.name}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-foreground/55 mt-1">
                  {t.role}
                </p>
              </div>
            </blockquote>
          ))}
        </div>
      </section>
      )}

      {/* Empty state */}
      {videoTestimonials.length === 0 && writtenTestimonials.length === 0 && (
        <section className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="testimonials-empty">
          <Quote className="text-primary/40 mx-auto" size={44} strokeWidth={1.3} />
          <h2 className="font-serif text-3xl md:text-4xl mt-6 leading-tight">
            Stories coming soon.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            Tammy is gathering testimonials from clients and coaching members.
            Check back — or if you've worked with her and want to share your
            story, she'd love to hear it.
          </p>
        </section>
      )}

      {/* CTA */}
      <section className="bg-muted/40 py-24 mt-6">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            Ready to write your own?
          </h2>
          <p className="text-lg text-foreground/75 mt-6">
            Book a discovery call and see what working with Tammy could
            actually look like.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <Link to="/booking" data-testid="testimonials-cta-book">
              <Button className="rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
                Book a Discovery Call <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact" data-testid="testimonials-cta-contact">
              <Button variant="outline" className="rounded-full h-12 px-8 border-border">
                Share your own story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Video modal */}
      <Dialog open={!!activeVideo} onOpenChange={(o) => !o && setActiveVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden rounded-2xl border-0" data-testid="video-modal">
          <DialogTitle className="sr-only">
            {activeVideo?.name || "Video testimonial"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Video testimonial for Tammy Richard.
          </DialogDescription>
          {activeVideo?.embedUrl && (
            <div className="aspect-video bg-black">
              <iframe
                src={activeVideo.embedUrl}
                title={activeVideo.name || "Video testimonial"}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideoCard({ v, i, onOpen }) {
  const hasVideo = !!v.embedUrl;
  return (
    <button
      type="button"
      onClick={() => onOpen(v)}
      disabled={!hasVideo}
      data-testid={`video-testimonial-${i}`}
      className={`group bg-card border border-border rounded-2xl overflow-hidden text-left card-lift ${
        !hasVideo ? "cursor-default opacity-90" : "cursor-pointer"
      }`}
    >
      <div className="relative aspect-video bg-muted overflow-hidden">
        {v.thumbnail ? (
          <img
            src={v.thumbnail}
            alt={v.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/15 to-secondary/20">
            <Play className="text-primary/40" size={48} strokeWidth={1.4} />
          </div>
        )}
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground/20 opacity-0 group-hover:opacity-100 transition">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-xl">
              <Play className="text-white ml-1" size={22} fill="currentColor" />
            </div>
          </div>
        )}
      </div>
      <div className="p-6">
        {hasVideo ? (
          <>
            <h3 className="font-serif text-xl leading-tight">
              {v.name || "Client Story"}
            </h3>
            {v.role && (
              <p className="text-xs uppercase tracking-[0.18em] text-foreground/55 mt-2">
                {v.role}
              </p>
            )}
          </>
        ) : (
          <>
            <h3 className="font-serif text-xl leading-tight text-foreground/70">
              Coming soon
            </h3>
            <p className="text-xs uppercase tracking-[0.18em] text-foreground/45 mt-2">
              New video testimonial
            </p>
          </>
        )}
      </div>
    </button>
  );
}
